import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CogIcon, ClipboardDocumentIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { TOKEN_SYMBOL, TOKEN_DECIMALS } from '../config/token';

interface TokenInfo {
  mintAddress: string;
  decimals: number;
  totalSupply: number;
}

interface HotWalletInfo {
  balance: number;
  publicKey: string;
  solBalance: number;
}

const TokenManagement: React.FC = () => {
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [hotWalletInfo, setHotWalletInfo] = useState<HotWalletInfo | null>(null);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    fetchTokenInfo();
  }, []);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const fetchTokenInfo = async () => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
      const [tokenRes, balanceRes, publicKeyRes, solBalanceRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/token/info`),
        axios.get(`${API_BASE_URL}/token/hot-wallet/balance`),
        axios.get(`${API_BASE_URL}/token/hot-wallet/public-key`),
        axios.get(`${API_BASE_URL}/token/hot-wallet/sol-balance`),
      ]);
      setTokenInfo(tokenRes.data);
      setHotWalletInfo({
        balance: balanceRes.data.balance,
        publicKey: publicKeyRes.data.publicKey,
        solBalance: solBalanceRes.data.solBalance,
      });
    } catch (error) {
      console.error('Error fetching token info:', error);
    } finally {
      setLoading(false);
    }
  };



  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-slate-100">Token Management</h1>
          <p className="mt-2 text-sm text-slate-400">
            Manage {TOKEN_SYMBOL} token and view token information
          </p>
        </div>

      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        {/* Token Information */}
        <div className="rounded-xl border border-slate-700 bg-slate-800 shadow-sm overflow-hidden">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CogIcon className="h-6 w-6 text-slate-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-slate-400 truncate">
                    {TOKEN_SYMBOL} Token Info
                  </dt>
                  <dd className="text-lg font-medium text-slate-100">
                    {tokenInfo ? (
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm text-slate-400">Mint Address:</span>
                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-xs font-mono break-all flex-1">{tokenInfo.mintAddress}</p>
                            <button
                              onClick={() => copyToClipboard(tokenInfo.mintAddress)}
                              className="p-1 text-slate-400 hover:text-indigo-400"
                              title="Copy to clipboard"
                            >
                              <ClipboardDocumentIcon className="h-5 w-5" />
                            </button>
                          </div>
                          <div className="flex gap-2 mt-2">
                            <a
                              href={`https://explorer.solana.com/address/${tokenInfo.mintAddress}?cluster=devnet`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-xs text-blue-400 hover:text-blue-300"
                            >
                              <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1" />
                              View on Devnet Explorer
                            </a>
                            <a
                              href={`https://explorer.solana.com/address/${tokenInfo.mintAddress}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-xs text-blue-400 hover:text-blue-300"
                            >
                              <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1" />
                              View on Mainnet Explorer
                            </a>
                          </div>
                        </div>
                        <div>
                          <span className="text-sm text-slate-400">Decimals:</span>
                          <p>{tokenInfo.decimals}</p>
                        </div>
                        <div>
                          <span className="text-sm text-slate-400">Total Supply:</span>
                          <p>{tokenInfo.totalSupply.toLocaleString()} {TOKEN_SYMBOL}</p>
                        </div>
                      </div>
                    ) : (
                      'Not created yet'
                    )}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Hot Wallet Information */}
        <div className="rounded-xl border border-slate-700 bg-slate-800 shadow-sm overflow-hidden">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CogIcon className="h-6 w-6 text-slate-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-slate-400 truncate">
                    Hot Wallet Info
                  </dt>
                  <dd className="text-lg font-medium text-slate-100">
                    {hotWalletInfo ? (
                      <div className="space-y-3">
                        <div>
                          <span className="text-sm text-slate-400">{TOKEN_SYMBOL} Balance:</span>
                          <p>{hotWalletInfo.balance.toLocaleString()} {TOKEN_SYMBOL}</p>
                        </div>
                        <div>
                          <span className="text-sm text-slate-400">SOL Balance:</span>
                          <p>{hotWalletInfo.solBalance.toFixed(4)} SOL</p>
                        </div>
                        <div>
                          <span className="text-sm text-slate-400">Public Key:</span>
                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-xs font-mono break-all flex-1">{hotWalletInfo.publicKey}</p>
                            <button
                              onClick={() => copyToClipboard(hotWalletInfo.publicKey)}
                              className="p-1 text-slate-400 hover:text-indigo-400"
                              title="Copy to clipboard"
                            >
                              <ClipboardDocumentIcon className="h-5 w-5" />
                            </button>
                          </div>
                          <div className="flex gap-2 mt-2">
                            <a
                              href={`https://explorer.solana.com/address/${hotWalletInfo.publicKey}?cluster=devnet`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-xs text-blue-400 hover:text-blue-300"
                            >
                              <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1" />
                              View on Devnet Explorer
                            </a>
                            <a
                              href={`https://explorer.solana.com/address/${hotWalletInfo.publicKey}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-xs text-blue-400 hover:text-blue-300"
                            >
                              <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1" />
                              View on Mainnet Explorer
                            </a>
                          </div>
                        </div>
                      </div>
                    ) : (
                      'Not available'
                    )}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Token Specifications */}
      <div className="rounded-xl border border-slate-700 bg-slate-800 p-6 shadow-sm">
        <h3 className="text-lg leading-6 font-medium text-slate-100 mb-4">
          {TOKEN_SYMBOL} Token Specifications
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="text-sm font-medium text-slate-400">Token Details</h4>
            <ul className="mt-2 text-sm text-slate-200 space-y-1">
              <li>• Name: USD Coin</li>
              <li>• Symbol: {TOKEN_SYMBOL}</li>
              <li>• Issuer: Circle</li>
              <li>• Decimals: {TOKEN_DECIMALS}</li>
              <li>• Network: Solana Devnet (Mainnet Ready)</li>
              <li>• Standard: SPL Token (legacy)</li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-medium text-slate-400">Features</h4>
            <ul className="mt-2 text-sm text-slate-200 space-y-1">
              <li>• Fully-reserved USD stablecoin</li>
              <li>• Hot Wallet Management</li>
              <li>• Automated Transaction Processing</li>
              <li>• Callback Webhooks</li>
              <li>• Failed Transaction Retry</li>
              <li>• Fund Collection System</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TokenManagement;
